import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import client from "utils/client"

/**
 *
 * @param {number} id purchase product id
 */
export const usePurchaseProduct = (id) =>
  useQuery({
    queryKey: ["purchaseProducts", id],
    queryFn: async () => {
      const res = await client(`/purchase_products/${id}.json`)
      return res.data
    },
    enabled: !!id
  })

/**
 * @param {number} id
 * @returns {import("@tanstack/react-query").UseQueryResult<JassApi.V2.PriceEditors.PurchaseProducts.PurchaseProduct>}
 */
export const usePriceEditorsPurchaseProduct = (id) =>
  useQuery({
    queryKey: ["v2", "price_editors", "purchase_products", "show", id],
    queryFn: async () => {
      const res = await client.get(
        `/v2/price_editors/purchase_products/${id}.json`
      )
      return res.data
    },
    enabled: !!id
  })

export const useUpdatePriceEditorsPurchaseProduct = () => {
  const queryClient = useQueryClient()

  return useMutation({
    /**
     * @param {{ id: number; values: JassApi.V2.PriceEditors.PurchaseProducts.UpdateParams }} params
     */
    mutationFn: async ({ id, values }) => {
      const res = await client.put(
        `/v2/price_editors/purchase_products/${id}.json`,
        { purchase_product: values }
      )
      return res.data
    },
    onSuccess: (_response, { id }) => {
      queryClient.invalidateQueries({
        queryKey: ["v2", "price_editors", "purchase_products", "show", id]
      })
      queryClient.invalidateQueries({
        queryKey: ["policies", "V2::SalesProduct"]
      })
    }
  })
}

/**
 * @return {import("@tanstack/react-query").UseMutationResult}
 */
export const useCreatePurchaseProduct = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ values }) => {
      const response = await client.post("/purchase_products.json", {
        product: values
      })

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["purchaseProducts"])
      queryClient.invalidateQueries(["suppliers"])
    }
  })
}

/**
 * @return {import("@tanstack/react-query").UseMutationResult}
 */
export const useUpdatePurchaseProduct = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, values }) => {
      const response = await client.patch(`/purchase_products/${id}.json`, {
        product: values
      })

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["purchaseProducts"])
      queryClient.invalidateQueries(["suppliers"])
    }
  })
}

/**
 * @return {import("@tanstack/react-query").UseMutationResult<any, unknown, number, unknown>}
 */
export const useDeletePurchaseProduct = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id) => {
      const response = await client.delete(`/purchase_products/${id}.json`)
      return response.data
    },
    onSuccess() {
      queryClient.invalidateQueries(["salesProducts"])
      queryClient.invalidateQueries(["suppliers"])
      queryClient.invalidateQueries(["purchaseProducts"])
    }
  })
}
